import { useEffect, useState } from "react";
import { X } from "lucide-react";
import brandGuidelinesPreview from '@/assets/showcase/brand-guidelines-preview.webp';

// HARD DEADLINE - Promoção de Janeiro 2026
const EXPIRATION_DATE = new Date('2026-01-12T00:00:00-03:00');

const PromoBanner = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isVisible, setIsVisible] = useState(true);

  // Verificação IMEDIATA de expiração
  const isExpired = Date.now() > EXPIRATION_DATE.getTime();

  useEffect(() => {
    // Se já expirou, não faz nada
    if (isExpired) {
      setIsVisible(false);
      return;
    }

    // Check if banner was closed
    const bannerClosed = sessionStorage.getItem('promoBannerClosed');
    if (bannerClosed) {
      setIsVisible(false);
      return;
    }

    const endTimestamp = EXPIRATION_DATE.getTime();

    const updateCountdown = () => {
      const now = Date.now();
      const distance = endTimestamp - now;

      if (distance < 0) {
        setIsVisible(false);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [isExpired]);

  // EARLY RETURN: Se expirou ou foi fechado, não renderiza NADA
  if (isExpired || !isVisible) return null;

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem('promoBannerClosed', 'true');
  };

  return (
    <div className="sticky top-0 z-50 relative overflow-hidden">
      {/* Mesh Gradient Base - Sem brancos estourados */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 20% 40%, #F59E0B 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 80% 60%, #EA580C 0%, transparent 50%),
            linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)
          `
        }}
      />
      
      {/* Grain Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.12] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '128px 128px'
        }}
      />
      
      {/* Organic Blobs */}
      <div className="absolute -top-8 -left-8 w-32 h-32 bg-amber-400/40 rounded-full blur-2xl animate-blob-float" />
      <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-orange-500/30 rounded-full blur-xl animate-blob-float" style={{ animationDelay: '-7s' }} />
      
      {/* Content */}
      <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3 relative z-10">
        {/* MOBILE: Single line compact layout */}
        <div className="flex items-center justify-center gap-2 sm:gap-4">
          
          {/* Badge 80% OFF - Compact on mobile */}
          <span className="bg-gradient-to-r from-red-600 to-red-700 text-white font-black px-2 sm:px-4 py-1 sm:py-2 rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.08)] text-[10px] sm:text-sm uppercase tracking-wide animate-pulse flex-shrink-0">
            80% OFF
          </span>
          
          {/* Copy - One line mobile, full desktop */}
          <span className="text-amber-950 text-[10px] sm:text-xs font-bold truncate">
            <span className="sm:hidden">Promo Janeiro</span>
            <span className="hidden sm:inline lg:hidden">PROMO MAIS QUENTE DO ANO</span>
            <span className="hidden lg:inline">PROMO MAIS QUENTE DO ANO | As marcas de Janeiro vão ficar na pele e na mente.</span>
          </span>
          
          {/* Countdown - Compact */}
          <div className="flex items-center gap-0.5 sm:gap-1 font-mono text-[10px] sm:text-xs bg-white text-amber-950 px-2 sm:px-3 py-1 sm:py-2 rounded-lg sm:rounded-xl shadow-md flex-shrink-0">
            <span className="font-bold">{timeLeft.days}d</span>
            <span className="opacity-50">:</span>
            <span className="font-bold">{timeLeft.hours}h</span>
            <span className="hidden sm:inline opacity-50">:</span>
            <span className="hidden sm:inline font-bold">{timeLeft.minutes}m</span>
          </div>
        </div>
        
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-amber-950/10 bg-white/20 backdrop-blur-sm rounded-full transition-all duration-300"
          aria-label="Fechar banner"
        >
          <X className="w-4 h-4 text-amber-950" />
        </button>
      </div>
    </div>
  );
};

export default PromoBanner;
